'use client';

import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import {
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerFooter,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { openQuotaExceededUpgradeModal } from '@heidi/commerce/data';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import type { Dispatch } from '@/store';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useTranslations } from '@heidi/runtime/i18n';
import { FormattedMessage } from 'react-intl';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { track } from '@/lib/analytics/track';

export type AdvancedEvidenceQueryInfoModalVariant = 'clinician' | 'practice';

export interface AdvancedEvidenceQueryInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: AdvancedEvidenceQueryInfoModalVariant;
  /**
   * Prefer tier-aware upgrade routing (e.g. from chat footer). Falls back to
   * {@link openQuotaExceededUpgradeModal} when omitted.
   */
  onUpgrade?: () => void;
  /** User billing tier (`new_product_tier`) for analytics `current_plan`. */
  currentPlan?: SubscriptionTier | null;
}

const DELIVERABLES_LIST_MESSAGE_IDS = [
  { id: 'pQ1nR4vWxA', defaultMessage: 'Exporting a clinical note or document (PDF, Word, PowerPoint, Excel, CSV)' },
  { id: 'pQ2nR4vWxB', defaultMessage: 'Generating or emailing a patient evidence PDF' },
  { id: 'pQ3nR4vWxC', defaultMessage: 'Filling a PDF form for a patient' },
  { id: 'pQ4nR4vWxD', defaultMessage: 'Editing a presentation in Heidi' },
] as const;

const UNLIMITED_LIST_MESSAGE_IDS = [
  { id: 'uL1nR4vWxA', defaultMessage: 'Recording sessions and generating clinical notes' },
  { id: 'uL2nR4vWxB', defaultMessage: 'Editing notes with AI prompts' },
  {
    id: 'uL3nR4vWxC',
    defaultMessage: 'Generating custom documents like referrals, letters and care plans',
  },
  { id: 'uL4nR4vWxD', defaultMessage: 'Using and customising templates' },
  { id: 'uL5nR4vWxE', defaultMessage: 'Creating tasks and reminders' },
  { id: 'uL6nR4vWxF', defaultMessage: 'Searching your templates, patients and sessions' },
  { id: 'uL7nR4vWxG', defaultMessage: 'Viewing patient and EHR data' },
  { id: 'uL8nR4vWxH', defaultMessage: 'Asking Heidi outside of a session' },
] as const;

function CreditsInfoModalBody({
  variant,
}: {
  variant: AdvancedEvidenceQueryInfoModalVariant;
}) {
  const isPractice = variant === 'practice';

  return (
    <div className="flex flex-col gap-4 pe-1 text-text-secondary">
      <TypographyP2Bold className="font-bold">
        {isPractice ? (
          <FormattedMessage
            id="hPw1cR3kLm"
            defaultMessage="How Practice credits work"
            description="Subheading explaining Practice credits in credits info modal"
          />
        ) : (
          <FormattedMessage
            id="hCw1cR3kLm"
            defaultMessage="How Clinician credits work"
            description="Subheading explaining Clinician credits in credits info modal"
          />
        )}
      </TypographyP2Bold>

      <TypographyP2>
        {isPractice ? (
          <FormattedMessage
            id="iPw2cR3kLm"
            defaultMessage="Spend Practice credits on Heidi's advanced clinical tools. Each action spends one credit. Your plan includes 10 credits per user, per week, and your balance resets every 7 days."
            description="Intro paragraph for Practice credits info modal"
          />
        ) : (
          <FormattedMessage
            id="iCw2cR3kLm"
            defaultMessage="Spend Clinician credits on Heidi's advanced clinical tools. Each action spends one credit. Your plan includes 10 credits per week, and your balance resets every 7 days."
            description="Intro paragraph for Clinician credits info modal"
          />
        )}
      </TypographyP2>

      <TypographyP2Bold className="font-bold">
        <FormattedMessage
          id="wSp3cR3kLm"
          defaultMessage="What spends a credit"
          description="Subheading before what counts toward credits"
        />
      </TypographyP2Bold>

      <TypographyP2>
        <FormattedMessage
          id="wSp4cR3kLm"
          defaultMessage="Every question or prompt you send Heidi mid-session, including clinical questions, differentials, drug doses, clinical calculators, diagnostic support and live EHR lookups. Tapping a transcript suggestion that asks Heidi a question also counts."
          description="Paragraph explaining mid-session credit usage"
        />
      </TypographyP2>

      <TypographyP2>
        <FormattedMessage
          id="gDv5cR3kLm"
          defaultMessage="Generating deliverables"
          description="Subheading before deliverables list in credits info modal"
        />
      </TypographyP2>

      <ul className="list-disc ps-5">
        {DELIVERABLES_LIST_MESSAGE_IDS.map((message) => (
          <li key={message.id} className="marker:text-text-tertiary">
            <TypographyP2>
              <FormattedMessage id={message.id} defaultMessage={message.defaultMessage} />
            </TypographyP2>
          </li>
        ))}
      </ul>

      <TypographyP2Bold className="font-bold">
        <FormattedMessage
          id="aUl6cR3kLm"
          defaultMessage="Always unlimited"
          description="Subheading before unlimited actions list in credits info modal"
        />
      </TypographyP2Bold>

      <ul className="list-disc ps-5">
        {UNLIMITED_LIST_MESSAGE_IDS.map((message) => (
          <li key={message.id} className="marker:text-text-tertiary">
            <TypographyP2>
              <FormattedMessage id={message.id} defaultMessage={message.defaultMessage} />
            </TypographyP2>
          </li>
        ))}
      </ul>

      <TypographyP2>
        <FormattedMessage
          id="oUt7cR3kLm"
          defaultMessage="When credits run out, the rest of Heidi keeps working. Advanced tools come back when your credits refresh."
          description="Paragraph explaining behavior when credits are exhausted"
        />
      </TypographyP2>

      <TypographyP2>
        {isPractice ? (
          <FormattedMessage
            id="cPw8cR3kLm"
            defaultMessage="To use credits worry-free, upgrade to Practice."
            description="Closing line in Practice credits info modal"
          />
        ) : (
          <FormattedMessage
            id="cCw8cR3kLm"
            defaultMessage="To use credits worry-free, upgrade to Clinician."
            description="Closing line in Clinician credits info modal"
          />
        )}
      </TypographyP2>
    </div>
  );
}

export function AdvancedEvidenceQueryInfoModal({
  open,
  onOpenChange,
  variant = 'clinician',
  onUpgrade,
  currentPlan,
}: AdvancedEvidenceQueryInfoModalProps) {
  const dispatch = useDispatch<Dispatch>();
  const { t } = useTranslations();
  const isPractice = variant === 'practice';
  const targetPlan = isPractice
    ? SubscriptionTier.CLINICIAN_PRO_PLUS
    : SubscriptionTier.CLINICIAN_PRO;
  const upgradeLabel = isPractice
    ? t('upgrade.upgradeToPractice')
    : t('upgrade.upgradeToClinician');

  useEffect(() => {
    if (!open) return;
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: {
        screen_name: ScreenName.AdvancedEvidenceQueryInfoModal,
        target_plan: targetPlan,
        ...(currentPlan !== undefined &&
          currentPlan !== null && { current_plan: currentPlan }),
      },
    });
    // Fire once per open; plan is read at impression time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleUpgrade = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.AdvancedEvidenceQueryInfoModal,
        action: ButtonAction.AdvancedEvidenceQueryInfoModalUpgrade,
        label: upgradeLabel,
        target_plan: targetPlan,
        ...(currentPlan !== undefined &&
          currentPlan !== null && { current_plan: currentPlan }),
      },
    });
    onOpenChange(false);
    queueMicrotask(() => {
      if (onUpgrade) {
        onUpgrade();
        return;
      }
      openQuotaExceededUpgradeModal();
    });
  }, [currentPlan, dispatch, onOpenChange, onUpgrade, targetPlan, upgradeLabel]);

  const handleClose = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.AdvancedEvidenceQueryInfoModal,
        action: ButtonAction.AdvancedEvidenceQueryInfoModalClose,
        label: 'Close',
        target_plan: targetPlan,
        ...(currentPlan !== undefined &&
          currentPlan !== null && { current_plan: currentPlan }),
      },
    });
    onOpenChange(false);
  }, [currentPlan, dispatch, onOpenChange, targetPlan]);

  return (
    <DialogDrawer open={open} onOpenChange={onOpenChange}>
      <DialogDrawerContent
        className={cn(
          'flex max-h-[90dvh] w-full max-w-[672px] flex-col gap-4 overflow-hidden border-none bg-surface-elevated p-6 shadow-2xl'
        )}
        overlayClassName="backdrop-blur-sm"
        data-testid="advanced-evidence-query-info-modal"
        data-variant={variant}
      >
        <DialogDrawerHeader className="shrink-0 gap-1.5 space-y-0 pe-10 text-start">
          <DialogDrawerTitle>
            {isPractice ? (
              <FormattedMessage
                id="tPw9cR3kLm"
                defaultMessage="Practice credits"
                description="Title for Practice credits info modal"
              />
            ) : (
              <FormattedMessage
                id="tCw9cR3kLm"
                defaultMessage="Clinician credits"
                description="Title for Clinician credits info modal"
              />
            )}
          </DialogDrawerTitle>
        </DialogDrawerHeader>

        <ScrollArea
          type="scroll"
          className="h-[min(382px,calc(90dvh-14rem))] min-h-0 w-full shrink-0 pe-10"
        >
          <CreditsInfoModalBody variant={variant} />
        </ScrollArea>

        <DialogDrawerFooter className="mt-0 flex w-full shrink-0 flex-row flex-wrap items-center justify-end gap-2 pt-3">
          <Button variant="ghost" size="md" type="button" onClick={handleClose}>
            <FormattedMessage
              id="QQfw1y1KXD"
              defaultMessage="Close"
              description="Button to close Advanced Evidence quota modal"
            />
          </Button>
          <Button
            variant={isPractice ? 'pro_plus' : 'pro'}
            size="md"
            type="button"
            onClick={handleUpgrade}
            className="gap-x-1.5"
          >
            {isPractice ? (
              <TierPracticeFilled className="shrink-0" />
            ) : (
              <TierProFilled className="shrink-0" />
            )}
            {upgradeLabel}
          </Button>
        </DialogDrawerFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
